/**
 * Pending email-OTP login step.
 *
 * The OTP step must survive a taskpane refresh, and the verify call re-sends the
 * password, so the pending step is kept in sessionStorage: it survives a reload
 * but is wiped when the taskpane/Excel closes, and the custom-functions runtime
 * never reads it. Bounded by a TTL and removed on success, back-to-login,
 * logout or expiry.
 */

export interface PendingVerification {
  username: string;
  password: string;
  serverMessage?: string;
  // When the latest code was emailed (ms) — drives the resend cooldown and the
  // expiry below.
  codeSentAt: number;
}

const PENDING_VERIFICATION_KEY = 'iad_pending_verification';
const PENDING_VERIFICATION_TTL_MS = 10 * 60 * 1000;

export function loadPendingVerification(): PendingVerification | null {
  try {
    const raw = sessionStorage.getItem(PENDING_VERIFICATION_KEY);
    if (!raw) return null;
    const pending = JSON.parse(raw) as PendingVerification;
    const isUsable = pending?.username && pending?.password && typeof pending.codeSentAt === 'number'
      && Date.now() - pending.codeSentAt < PENDING_VERIFICATION_TTL_MS;
    if (!isUsable) {
      sessionStorage.removeItem(PENDING_VERIFICATION_KEY);
      return null;
    }
    return pending;
  } catch {
    return null;
  }
}

export function savePendingVerification(pending: PendingVerification | null): void {
  try {
    if (pending) {
      sessionStorage.setItem(PENDING_VERIFICATION_KEY, JSON.stringify(pending));
    } else {
      sessionStorage.removeItem(PENDING_VERIFICATION_KEY);
    }
  } catch { /* storage unavailable — the OTP step just won't survive a reload */ }
}

export function clearPendingVerification(): void {
  savePendingVerification(null);
}
